// src/templates/modern/restaurant/components/RestaurantLocationRating.tsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaChevronDown,
  FaChevronUp,
  FaGoogle,
  FaExternalLinkAlt,
} from "react-icons/fa";
import type { Branch } from "~/types/new_types";
import { useGoogleMaps } from "~/contexts/GoogleMapsContext";

interface RestaurantLocationRatingProps {
  restaurant: Branch;
}

interface Review {
  authorName: string;
  rating: number;
  text: string;
  relativeTimeDescription: string;
  profilePhotoUrl?: string;
}

interface PlaceDetails {
  name: string;
  rating: number;
  totalRatings: number;
}

const RestaurantLocationRating: React.FC<RestaurantLocationRatingProps> = ({
  restaurant,
}) => {
  const { t } = useTranslation();
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();
  
  const [reviews, setReviews] = useState<Review[]>([]);
  const [placeDetails, setPlaceDetails] = useState<PlaceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;



 
  useEffect(() => {
    fetchPlaceDetails();
  }, [restaurant.location?.place_id, isGoogleMapsLoaded, apiKey]);

  const fetchPlaceDetails = async () => {
    if (!restaurant.location?.place_id) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await fetchWithModernAPI(restaurant.location.place_id);
      
      if (!success) {
        await fetchWithLegacyAPI(restaurant.location.place_id);
      }
    } catch (err) {
      setError("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const fetchWithModernAPI = async (placeId: string): Promise<boolean> => {
    try {
      if (!window.google?.maps?.importLibrary) {
           return false;
      }

     const placesLib = await window.google.maps.importLibrary('places');
      
      if (!(placesLib as any).Place) {
          return false;
      }

      const { Place } = placesLib as any;
        const place = new Place({
        id: placeId,
      });

      const modernFields: string[] = [
        'displayName',
        'rating',
        'userRatingCount',
        'reviews',
        'location'
      ];

       await place.fetchFields({
        fields: modernFields,
      });
    
      // Check if rating data is missing (API key permission issue)
      const hasRatingData = place.rating !== undefined && place.rating !== null;
      const hasReviews = place.reviews && place.reviews.length > 0;
      
      if (!hasRatingData) {
               return false;
      }

      // Extract reviews
      const fetchedReviews: Review[] = [];
      if (hasReviews) {
        for (let i = 0; i < place.reviews.length; i++) {
          const review = place.reviews[i];
          const reviewText = typeof review.text === 'string' ? review.text : (typeof review.text === 'object' && review.text ? (review.text as { text: string }).text : "");
          fetchedReviews.push({
            authorName: review.authorAttribution?.displayName || "Anonymous",
            rating: review.rating || 0,
            text: reviewText,
            relativeTimeDescription: review.relativePublishTimeDescription || "",
            profilePhotoUrl: review.authorAttribution?.photoURI || undefined,
          });
        }
       } 
      setReviews(fetchedReviews);
      setPlaceDetails({
        name: place.displayName || restaurant.name,
        rating: place.rating || 0,
        totalRatings: place.userRatingCount || 0,
      });
          return true;
    } catch (err) {
      return false;
    }
  };

  const fetchWithLegacyAPI = async (placeId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
       if (!window.google?.maps?.places?.PlacesService) {
         reject(new Error("Places service not available"));
        return;
      }

       const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
     
      const request = {
        placeId: placeId,
        fields: ["name", "rating", "user_ratings_total", "reviews"],
      };
  
      service.getDetails(
        request,
        (
          place: google.maps.places.PlaceResult | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            
            const fetchedReviews: Review[] = [];
            if (place.reviews && place.reviews.length > 0) {
              for (let i = 0; i < place.reviews.length; i++) {
                const review = place.reviews[i];
                fetchedReviews.push({
                  authorName: review.author_name || "Anonymous",
                  rating: review.rating || 0,
                  text: review.text || "",
                  relativeTimeDescription: review.relative_time_description || "",
                  profilePhotoUrl: review.profile_photo_url || undefined,
                });
              }
            }

            setReviews(fetchedReviews);
            setPlaceDetails({
              name: place.name || restaurant.name,
              rating: place.rating || 0,
              totalRatings: place.user_ratings_total || 0,
            });
            
            resolve();
          } else {
            reject(new Error(`Places service status: ${status}`));
          }
        }
      );
    });
  };

  const renderStars = (rating: number, size: "sm" | "md" | "lg" = "md") => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    const sizeClass = {
      sm: "text-sm",
      md: "text-base",
      lg: "text-xl",
    }[size];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <FaStar
            key={i}
            className={sizeClass}
            style={{ color: "#FFA500" }}
          />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <FaStarHalfAlt
            key={i}
            className={sizeClass}
            style={{ color: "#FFA500" }}
          />
        );
      } else {
        stars.push(
          <FaRegStar
            key={i}
            className={sizeClass}
            style={{ color: "#D1D5DB" }}
          />
        );
      }
    }

    return <div className="flex gap-1">{stars}</div>;
  };

  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  if (!restaurant.location?.place_id) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: restaurant.theme.cardColor,
        borderRadius: restaurant.theme.borderRadius,
        boxShadow: `0 10px 30px -10px ${restaurant.theme.primaryColor}20`,
      }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2
              className="text-2xl font-bold"
              style={{ color: restaurant.theme.primaryColor }}
            >
              {t("restaurant.reviews.title", "Customer Reviews")}
            </h2>
            <FaGoogle
              className="text-2xl"
              style={{ color: restaurant.theme.primaryColor }}
            />
          </div>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-lg transition-colors"
            style={{
              backgroundColor: `${restaurant.theme.primaryColor}10`,
              color: restaurant.theme.primaryColor,
            }}
            aria-label={isExpanded ? "Collapse reviews" : "Expand reviews"}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>

  
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderColor: restaurant.theme.primaryColor }}
                  />
                </div>
              ) : error ? (
                <div
                  className="text-center py-8"
                  style={{ color: `${restaurant.theme.textColor}80` }}
                >
                  {error}
                </div>
              ) : placeDetails && placeDetails.rating > 0 ? (
                <>
                  {/* Overall Rating */}
                  <div
                    className="flex flex-col items-center justify-center p-6 rounded-xl mb-6"
                    style={{
                      backgroundColor: `${restaurant.theme.primaryColor}10`,
                      borderRadius: restaurant.theme.borderRadius,
                    }}
                  >
                    <div
                      className="text-5xl font-bold mb-2"
                      style={{ color: restaurant.theme.primaryColor }}
                    >
                      {placeDetails.rating.toFixed(1)}
                    </div>
                    {renderStars(placeDetails.rating, "lg")}
                    <div
                      className="mt-2 text-sm"
                      style={{ color: `${restaurant.theme.textColor}80` }}
                    >
                      {t("restaurant.reviews.basedOn", {
                        count: placeDetails.totalRatings,
                        defaultValue: `Based on ${placeDetails.totalRatings} reviews`,
                      })}
                    </div>

                    {/* View/Write Review on Google Button */}
                    <a
                      href={`https://search.google.com/local/writereview?placeid=${restaurant.location?.place_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: restaurant.theme.primaryColor,
                        color: "#fff",
                        borderRadius: restaurant.theme.borderRadius,
                      }}
                    >
                      <FaGoogle className="text-lg" />
                      <span>{t("restaurant.reviews.writeReview", "Write a Review on Google")}</span>
                      <FaExternalLinkAlt className="text-sm" />
                    </a>
                  </div>

                  {/* Reviews List */}
                  {reviews.length > 0 ? (
                    <div className="space-y-4">
                      {displayedReviews.map((review, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-4 rounded-lg border"
                          style={{
                            backgroundColor: `${restaurant.theme.primaryColor}05`,
                            borderColor: `${restaurant.theme.primaryColor}20`,
                            borderRadius: restaurant.theme.borderRadius,
                          }}
                        >
                          {/* Review Header */}
                          <div className="flex items-start gap-3 mb-3">
                            {review.profilePhotoUrl ? (
                              <img
                                src={review.profilePhotoUrl}
                                alt={review.authorName}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-xs"
                                style={{
                                  backgroundColor: restaurant.theme.primaryColor,
                                }}
                              >
                                {(() => {
                                  const nameParts = review.authorName.trim().split(/\s+/);
                                  if (nameParts.length >= 2) {
                                    return (
                                      nameParts[0].charAt(0).toUpperCase() +
                                      nameParts[nameParts.length - 1].charAt(0).toUpperCase()
                                    );
                                  }
                                  return review.authorName.charAt(0).toUpperCase();
                                })()}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4
                                  className="font-semibold"
                                  style={{ color: restaurant.theme.textColor }}
                                >
                                  {review.authorName}
                                </h4>
                                <span
                                  className="text-sm"
                                  style={{
                                    color: `${restaurant.theme.textColor}60`,
                                  }}
                                >
                                  {review.relativeTimeDescription}
                                </span>
                              </div>
                              {renderStars(review.rating, "sm")}
                            </div>
                          </div>

                          {/* Review Text */}
                          {review.text && (
                            <p
                              className="text-sm leading-relaxed"
                              style={{ color: `${restaurant.theme.textColor}90` }}
                            >
                              {review.text}
                            </p>
                          )}
                        </motion.div>
                      ))}

                      {/* Show More/Less Button */}
                      {reviews.length > 3 && (
                        <button
                          onClick={() => setShowAllReviews(!showAllReviews)}
                          className="w-full py-3 rounded-lg font-medium transition-colors"
                          style={{
                            backgroundColor: `${restaurant.theme.primaryColor}10`,
                            color: restaurant.theme.primaryColor,
                            borderRadius: restaurant.theme.borderRadius,
                          }}
                        >
                          {showAllReviews
                            ? t("restaurant.reviews.showLess", "Show Less")
                            : t("restaurant.reviews.showMore", {
                                count: reviews.length - 3,
                                defaultValue: `Show ${reviews.length - 3} More Reviews`,
                              })}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      className="text-center py-8"
                      style={{ color: `${restaurant.theme.textColor}80` }}
                    >
                      {t("restaurant.reviews.noReviews", "No reviews available")}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="text-center py-12 px-6 rounded-xl"
                  style={{
                    backgroundColor: `${restaurant.theme.primaryColor}05`,
                    color: `${restaurant.theme.textColor}80`,
                    borderRadius: restaurant.theme.borderRadius,
                  }}
                >
                  <div className="text-4xl mb-4">📍</div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: restaurant.theme.textColor }}>
                    {t("restaurant.reviews.noRatingData", "No Reviews Yet")}
                  </h3>
                  <p className="text-sm mb-4">
                    {t("restaurant.reviews.noRatingMessage", "This location hasn't received any reviews on Google yet.")}
                  </p>
                  <a
                    href={`https://search.google.com/local/writereview?placeid=${restaurant.location?.place_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
                    style={{
                      backgroundColor: restaurant.theme.primaryColor,
                      color: "#fff",
                      borderRadius: restaurant.theme.borderRadius,
                    }}
                  >
                    <FaGoogle className="text-lg" />
                    <span>{t("restaurant.reviews.beFirst", "Be the First to Review")}</span>
                    <FaExternalLinkAlt className="text-sm" />
                  </a>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default RestaurantLocationRating;
